<template>
	<div class="form-designer-content">
		<Accordion title="Form Builder" v-model="accordion.first">
			<template #body-content>
				<div class="form-builder">
					<!-- NOTE FOR DEV: To collapse the form and show the maximize button, add is-closed class to section tag -->
					<section
						class="form-builder__add-fields"
						:class="[!openNewFieldFormData ? 'is-closed' : '']"
					>
						<div class="form-builder__expand-btn">
							<Button
								className="btn--has-icon"
								labelText="Add More Fields"
								:btnClick="openCloseImpactNewField"
								:iconBeforeLabel="false"
								:iconClassName="'icon-maximize'"
							/>
						</div>
						<div
							class="form-builder__fields-container"
							v-if="openNewFieldFormData"
						>
							<div class="form-builder__fields-header">
								<span class="form-builder__fields-title"
									>New Field</span
								>
								<Button
									:className="'btn--icon-only'"
									:btnClick="openCloseImpactNewField"
									:iconBeforeLabel="true"
									iconClassName="icon-minimize"
								/>
							</div>
							<NewFieldDetail
								v-model="fieldTypeObj"
								@add="addFileType"
								:impactInputFieldsList="impactInputFieldsList"
							/>
						</div>

						<!-- Add field-detail component -->
					</section>

					<section class="form-builder__reference">
						<div class="form-builder__reference-toolbar">
							<Search
								variant="toggle-icons"
								v-model="searchReference"
								placeholder="Search by reference or ID"
								:enableTimeOut="false"
							/>
							<Toggle
								:labelInsideClassName="'toggle--label-in'"
								:label="'Edit R/D/C'"
								v-model="canEdit"
								:isLabelBefore="true"
								:disabled="!hasResults"
							>
								<template slot="true-value">on</template>
								<template slot="false-value">off</template>
							</Toggle>
						</div>

						<div
							class="form-builder__reference-table"
							v-if="hasResults"
						>
							<!-- DEV: these components and action buttons should show based on the field type. Please see requirements for details.
							** For this table the column R; D; and C column labels, per initial discussion with dev, for mobile only the labels should read the entire word: Row; Dot; and Column. R; D; and C should show fo rtablet and up.
							-->
							<Table
								:sourceObject="tableSource"
								:editable="canEdit"
								@change="updatePagination"
								ref="tableForms"
								class="table-container--form-builder"
							>
								<template #col-group>
									<colgroup>
										<col
											class="table__col table__col--field-ref"
										/>
										<!-- Field Reference -->
										<col
											class="table__col table__col--field-type"
										/>
										<!-- Field Type -->
										<col
											class="table__col table__col--row"
										/>
										<!-- Row -->
										<col
											class="table__col table__col--dot"
										/>
										<!-- Dot -->
										<col
											class="table__col table__col--column"
										/>
										<!-- Column -->
										<col
											class="table__col table__col--actions"
										/>
										<!-- Actions -->
									</colgroup>
								</template>
								>
								<template #body-fieldReference="{ body }">
									<!-- DEV: display tooltip only if using ellipsis when the max chacrter has been reached.  -->

									<div
										class="form-builder__reference-field"
										v-resize="handleResize"
									>
										<Ellipsis
											:data="formFieldReferences(body)"
										></Ellipsis>
									</div>
								</template>
								<template #body-fieldType="{ body }">
									<!-- DEV: the Fied Type text needs to be shown for this column. Hardcoded for testing. Only the Customer Input text needs to show the Badge -->
									<div class="form-builder__type">
										<span class="form-builder__type-text">
											{{
												formImpactType(body.impactType)
											}}
											<BadgeCount
												:value="body.order"
												v-if="body.impactType === 'I'"
											/>
										</span>
									</div>
								</template>
								<template #body-row="{ body }">
									<div>
										<Input
											className="field__input"
											inputType="Number"
											v-if="canEdit"
											:minValue="0"
											:isNegativeAllow="false"
											:showFieldTextError="false"
											v-model="body.row"
											:min="0"
											:max="400"
											:forceCustomErrorMessage="
												errorRowColMessage(body.row)
											"
										/>
										<div v-else>
											{{ body.row }}

											<NotificationIcon
												v-show="
													Math.sign(body.row) === -1
												"
												:variant="
													hasRowColErrorValue(
														body.row
													)
												"
											/>
										</div>
									</div>
								</template>
								<template #body-dot="{ body }">
									<div>
										<Input
											className="field__input"
											inputType="Number"
											v-if="canEdit"
											:minValue="0"
											:maxValue="11"
											:isNegativeAllow="false"
											:showFieldTextError="false"
											v-model="body.dot"
											:min="0"
											:max="11"
											:forceCustomErrorMessage="
												errorDotMessage(body.dot)
											"
										/>
										<span v-else>
											{{ body.dot }}
											<NotificationIcon
												v-show="
													Math.sign(body.dot) ===
														-1 || body.dot > 11
												"
												:variant="
													hasDotErrorValue(body.dot)
												"
											/>
										</span>
									</div>
								</template>

								<template #body-col="{ body }">
									<div>
										<Input
											className="field__input"
											inputType="Number"
											v-if="canEdit"
											:minValue="0"
											:isNegativeAllow="false"
											:showFieldTextError="false"
											v-model="body.col"
											:min="0"
											:max="200"
											:forceCustomErrorMessage="
												errorRowColMessage(body.col)
											"
										/>
										<div v-else>
											{{ body.col }}
											<NotificationIcon
												v-show="
													Math.sign(body.col) === -1
												"
												:variant="
													hasRowColErrorValue(
														body.col
													)
												"
											/>
										</div>
									</div>
								</template>

								<template #body-actions="{ body }">
									<span class="table__action-btns">
										<ViewCode
											:popoverText="body.description"
											v-if="body.impactType === 'S'"
										>
										</ViewCode>
										<Button
											:className="'btn--icon-only-circle'"
											labelText=""
											:btnClick="
												() => {
													showFieldDetails(body.id);
												}
											"
											iconClassName="icon-info"
											:iconBeforeLabel="true"
											v-if="body.impactType === 'C'"
										/>

										<Button
											:className="'btn--icon-only-circle'"
											labelText=""
											:btnClick="showPromptOrderModal"
											iconClassName="icon-sort-set"
											:iconBeforeLabel="true"
											v-if="body.impactType === 'I'"
										/>
										<Button
											:className="'btn--icon-only-circle'"
											labelText=""
											:btnClick="
												() => {
													getField(body);
												}
											"
											iconClassName="icon-edit"
											:iconBeforeLabel="true"
										/>
										<Button
											:className="'btn--icon-only-circle'"
											labelText=""
											:btnClick="
												() => {
													showModalRemoveField(body);
												}
											"
											iconClassName="icon-delete-glenn"
											:iconBeforeLabel="true"
										/>
									</span>
								</template>
							</Table>
						</div>
						<div
							class="form-builder__reference form-builder__reference--no-results"
							v-else
						>
							No Fields Added!
						</div>
						<FieldEditFlyout
							:display="showFlyout"
							@show="handleShow"
							:fieldData="field"
							:impactInputFieldsList="impactInputFieldsList"
							:snippet="snippet"
							@change="
								val => {
									editFileType(val);
								}
							"
						>
						</FieldEditFlyout>
						<CodeDetailsModal
							:display="showCodeDetailModal"
							:snippet="codeSelectedDetails"
						/>
						<Modals
							v-model="showConfirmationModal"
							type="confirmation"
							:title="
								'Are you sure you want to delete this field?'
							"
							labelConfirmButton="Yes, delete field"
							labelCancelButton="Cancel"
							@confirm="removeField"
							@cancel="cancelRemoveField"
						/>

						<PromptOrderModal
							v-model="displayModalPromptOrder"
							:inputFields="inputFieldsData"
							@change="onChangeInputFieldList"
						/>
					</section>
				</div>
			</template>
		</Accordion>
		<Accordion
			title="Pre-Assign Clients"
			v-model="accordion.second"
			v-if="showPreAssignCustomer"
		>
			<template #body-content>
				<div class="pre-assign-customers">
					<p class="pre-assign-customers__description">
						Pre-assign clients to a form before it is Published.
						Once pre-assigned clients are Published, they can not be
						unassigned. However, they can be "disabled" from using
						the form.
					</p>
					<CustomerAssignation v-model="source.customers" />
				</div>
			</template>
		</Accordion>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import Input from '@/components/forms/input/input';
import Toggle from '@/components/forms/selection-control/toggle/toggle';
import Search from '@/components/forms/search/search';
import Table from '@/components/shells/table/table';
import BadgeCount from '@/components/visual/badge-count';
import Ellipsis from '@/components/visual/ellipsis';
import Accordion from '@/components/shells/accordion/accordion';
import resize from 'vue-resize-directive';
import FieldEditFlyout from '@/views/form-designer/impact-form-edit-flyout';
import NewFieldDetail from '@/views/form-designer/new-field-detail';
import NotificationIcon from '@/components/notifications/notification-icon/notification-icon';
import ViewCode from './view-code';
import CodeDetailsModal from '@/views/form-designer/code-details-modal.vue';
import { mapGetters } from 'vuex';
import Modals from '@/components/shells/modal/modals';
import CustomerAssignation from '@/sections/forms/views/customer-assignation.vue';
import Vue from 'vue';
import PromptOrderModal from '@/views/form-designer/impact-form-prompt-order-modal';
import { getNextNumber } from '@/utils/function';
import { isEmpty } from '@/utils/object';
import ImpactContent from '@/mixins/forms/impact-content';

export default {
	name: 'ImpactFormContent',
	mixins: [ImpactContent],
	components: {
		Button,
		Accordion,
		Input,
		Toggle,
		Search,
		Table,
		BadgeCount,
		Ellipsis,
		FieldEditFlyout,
		NewFieldDetail,
		NotificationIcon,
		ViewCode,
		CodeDetailsModal,
		Modals,
		CustomerAssignation,
		PromptOrderModal
	},
	model: {
		prop: 'source'
	},
	props: {
		label: {
			type: String,
			default: ''
		},
		source: {
			type: Object
		},
		editionMode: {
			type: Boolean,
			default: false
		}
	},
	directives: {
		resize
	},

	data() {
		return {
			accordion: {
				first: false,
				second: false
			},
			searchReference: '',
			hasErrorModal: true,
			formCreateValue: {
				text: 'Add Code',
				value: 'default'
			},
			impactInputFieldsList: [],
			tableSource: {
				headers: [
					{
						key: 'fieldReference',
						label: 'Field Reference',
						sorteable: false
					},
					{
						key: 'fieldType',
						label: 'Field Type',
						sorteable: false
					},
					{
						key: 'row',
						label: 'R',
						sorteable: false
					},
					{
						key: 'dot',
						label: 'D',
						sorteable: false
					},
					{
						key: 'col',
						label: 'C',
						sorteable: false
					},
					{
						key: 'actions',
						label: 'Actions',
						sorteable: false
					}
				],
				displayHeader: true,
				source: {}
			},
			canEdit: false,
			fieldTypeObj: {},
			radiobuttonItems: [
				{
					labelText: 'Enable',
					disabled: false
				},
				{
					labelText: 'Disable',
					disabled: false
				}
			],
			dataPage: 1,
			pageSize: 10,
			showFlyout: false,
			field: {},
			snippet: {},
			showConfirmationModal: false,
			inputFieldsData: [],
			displayModalPromptOrder: false,
			openNewFieldFormData: true
		};
	},
	computed: {
		...mapGetters('codemanager', [
			'showCodeDetailModal',
			'codeSelectedDetails',
			'codeSelected'
		]),
		...mapGetters('ellipsisResize', ['resize']),

		showPreAssignCustomer() {
			if (this.editionMode) {
				return !this.published && this.source.status === 'U';
			}
			return true;
		},
		isDesktopDevice() {
			return this.screenSize.desktop;
		}
	},
	methods: {
		handleResize(e) {
			const size = parseInt(e.offsetWidth);
			if (this.resize !== size) {
				this.$store.commit('ellipsisResize/setResize', size);
			}
		},
		openCloseImpactNewField() {
			this.openNewFieldFormData = !this.openNewFieldFormData;
		},
		addFileType() {
			let newFileTypeObj = { ...this.fieldTypeObj };
			if (!newFileTypeObj.truncate) {
				newFileTypeObj.characterNumber = 0;
			}

			if (!isEmpty(newFileTypeObj)) {
				if (!newFileTypeObj.impactType) {
					let impacts = [...this.impactInputFieldsList];
					Object.assign(
						newFileTypeObj,
						this.handlingFieldTypeUndefined(impacts)
					);
				}
				if (newFileTypeObj.impactType === 'I') {
					let arr = [...this.impactInputFieldsList];
					// formsKeyboardFieldsId
					const { keyboardPrompt } = newFileTypeObj || {};
					let existFieldType = arr.filter(item => {
						return item.keyboardPrompt === keyboardPrompt;
					});

					//exist keyboardPrompt
					if (existFieldType.length > 0) {
						let { formsKeyboardFieldsId } = existFieldType[0] || {};
						Object.assign(newFileTypeObj, {
							order: existFieldType[0].order,
							formsKeyboardFieldsId: formsKeyboardFieldsId
						});
					} else {
						//new  keyboardPrompt
						let formsKeyboardFieldsId = -Math.floor(
							Math.random() * 1000
						);
						let nextOrder = getNextNumber(
							arr.map(item => item.order)
						);
						Object.assign(newFileTypeObj, {
							order: nextOrder,
							formsKeyboardFieldsId: formsKeyboardFieldsId
						});
					}
					Object.assign(newFileTypeObj, {
						description: keyboardPrompt
					});
				}

				let nextId = getNextNumber(
					this.impactInputFieldsList.map(item => item.id),
					true
				);
				let formsRepositoryId = Number(this.$route.params.id);

				// assign to all new field
				Object.assign(newFileTypeObj, {
					id: nextId,
					formsRepositoryId: formsRepositoryId
				});

				this.impactInputFieldsList.push(newFileTypeObj);
				this.resetNewFieldForm();
			}
		},
		handlingField(obj) {
			const { impactType } = obj || {};
			if (impactType === 'I') {
				let arr = [...this.impactInputFieldsList];
				// formsKeyboardFieldsId
				const { keyboardPrompt } = obj || {};
				let existFieldType = arr.filter(item => {
					return item.keyboardPrompt === keyboardPrompt;
				});
				//exist keyboardPrompt
				if (existFieldType.length > 0) {
					let { formsKeyboardFieldsId } = existFieldType[0] || {};
					Object.assign(obj, {
						order: existFieldType[0].order,
						formsKeyboardFieldsId: formsKeyboardFieldsId
					});
				} else {
					//new  keyboardPrompt
					let formsKeyboardFieldsId = -Math.floor(
						Math.random() * 1000
					);
					let nextOrder = getNextNumber(arr.map(item => item.order));
					Object.assign(obj, {
						order: nextOrder,
						formsKeyboardFieldsId: formsKeyboardFieldsId
					});
				}
				Object.assign(obj, {
					description: keyboardPrompt
				});
				return obj;
			}
		},
		handlingFieldTypeUndefined(list) {
			let hasFieldTypeUndefined = list.some(item =>
				item.description.toLowerCase().includes('field')
			);
			let hightCounter = 0;

			if (hasFieldTypeUndefined) {
				let impacts = list.filter(impact =>
					impact.description.toLowerCase().includes('field')
				);
				let maxFieldReferenceObject = impacts.reduce(
					(prev, current) => {
						let strAfterSpace = current.description.substr(
							current.description.indexOf(' ') + 1
						);

						let strAfterSpacePrev = prev.description.substr(
							current.description.indexOf(' ') + 1
						);

						return Number(strAfterSpace) > Number(strAfterSpacePrev)
							? current
							: prev;
					}
				);
				let lastFieldNumber = maxFieldReferenceObject.description.substr(
					maxFieldReferenceObject.description.indexOf(' ') + 1
				);
				hightCounter = Number(lastFieldNumber);
			}
			hightCounter++;
			return Object.assign(
				{},
				{
					description: `field ${hightCounter}`
				}
			);
		},
		editFileType(obj) {
			if (!obj.truncate) {
				obj.characterNumber = 0;
			}
			const index = this.impactInputFieldsList.findIndex(
				item => item.id === obj.id
			);
			const { impactType } = obj || {};
			if (impactType === 'I' || impactType === 'S') {
				Object.assign(obj, this.handlingField(obj));
				this.impactInputFieldsList.splice(index, 1, obj);
			} else if (impactType === 'C') {
				this.impactInputFieldsList.splice(index, 1, obj);
			} else {
				let impacts = [...this.impactInputFieldsList];
				Object.assign(obj, this.handlingFieldTypeUndefined(impacts));
				const updatedImpactField = this.impactInputFieldsList.map(
					item => (item.id === obj.id ? Object.assign({}, obj) : item)
				);
				this.impactInputFieldsList = updatedImpactField;
			}
		},
		resetNewFieldForm() {
			this.fieldTypeObj = {};
		},
		getField(obj) {
			this.field = obj;

			if (obj.impactType === 'C') {
				const {
					attribute,
					calc,
					justify,
					format,
					description,
					formsSnippetsId,
					canBeUpdated,
					canBeDeleted
				} = obj;
				Object.assign(this.snippet, {
					id: formsSnippetsId,
					description: description,
					snippetAttribute: attribute,
					snippetCalc: calc,
					snippetJustify: justify,
					snippetFormat: format,
					canBeUpdated: canBeUpdated,
					canBeDeleted: canBeDeleted
				});
			}
			this.showFlyout = true;
		},
		handleShow(obj) {
			this.showFlyout = obj.display;
			this.snippet = {};
			this.field = {};
		},
		showModalRemoveField(obj) {
			this.field = obj;
			this.showConfirmationModal = true;
		},
		removeField() {
			const { id } = this.field || {};
			if (id) {
				const idx = this.impactInputFieldsList.findIndex(
					field => field.id === id
				);
				if (idx > -1) {
					this.impactInputFieldsList.splice(idx, 1);
				}
			}
			this.cancelRemoveField();
		},
		cancelRemoveField() {
			this.field = {};
			this.showConfirmationModal = false;
		},
		showFieldDetails(id) {
			let fieldDetails = this.impactInputFieldsList.find(
				field => field.id === id
			);

			const {
				attribute,
				calc,
				justify,
				format,
				description,
				formsSnippetsId
			} = fieldDetails || {};
			let code = Object.assign(
				{},
				{
					id: formsSnippetsId,
					description: description,
					snippetAttribute: attribute,
					snippetCalc: calc,
					snippetJustify: justify,
					snippetFormat: format
				}
			);
			this.$store.dispatch('codemanager/codeSelectedDetails', code);
			this.$store.dispatch('codemanager/showCodeDetailModal', true);
		},
		generateInputFieldsData(fieldsList) {
			let allInputs = [...fieldsList];
			let customerInputs = allInputs.filter(
				field => field.impactType === 'I'
			);
			let inputFields = customerInputs.map(item => {
				let { order } = item || {};
				return {
					keyboardPrompt: item.description,
					order: order,
					formsKeyboardFieldsId: item.formsKeyboardFieldsId
				};
			});
			let noInputsDuplicates = inputFields.reduce(
				(list, currentInput) => {
					if (
						!list.some(
							item =>
								item.formsKeyboardFieldsId ===
								currentInput.formsKeyboardFieldsId
						)
					) {
						list.push(currentInput);
					}
					return list;
				},
				[]
			);
			let inputFieldsSorted = this.sortedResponse(
				[...noInputsDuplicates],
				['order']
			);
			this.inputFieldsData = [...inputFieldsSorted];
		},
		onChangeInputFieldList(list) {
			let impactsList = [...this.impactInputFieldsList];
			list.forEach(input => {
				impactsList.forEach(customerInput => {
					if (
						input.formsKeyboardFieldsId ===
						customerInput.formsKeyboardFieldsId
					) {
						let { order } = input || {};
						Object.assign(customerInput, {
							order: order
						});
					}
				});
			});
			this.inputFieldsData = [...list];
			this.impactInputFieldsList = [...impactsList];
		},
		showPromptOrderModal() {
			this.displayModalPromptOrder = true;
		},
		handlerFieldUndefined(list) {
			let items = [];
			items = [...list];
			let itemsSorted = this.sortedResponse([...items], ['row', 'col']);
			let hightCounter = 0;
			itemsSorted.forEach(item => {
				if (!item.impactType) {
					hightCounter++;
					Object.assign(item, {
						description: `field ${hightCounter}`
					});
					return;
				}
			});
			this.impactInputFieldsList = [...itemsSorted];
		}
	},
	watch: {
		source: {
			deep: true,
			immediate: true,
			handler(obj) {
				if (obj.impactInputFields && obj.impactInputFields.length) {
					if (
						JSON.stringify(obj.impactInputFields) !==
						JSON.stringify(this.impactInputFieldsList)
					) {
						let impactList = [...obj.impactInputFields];
						let hasFieldTypeUndefined = impactList.some(
							item => !item.impactType
						);
						if (hasFieldTypeUndefined) {
							// at least it has a impactType = null
							this.handlerFieldUndefined(impactList);
						} else {
							this.impactInputFieldsList = [...impactList];
						}
					}
				}
			}
		},
		impactInputFieldsList: {
			deep: true,
			immediate: true,
			handler(list) {
				if (!this.canEdit) {
					this.generateSource(this.dataPage, this.pageSize);
				}
				this.generateInputFieldsData(list);
				Vue.set(this.source, 'impactInputFields', list);
			}
		},
		canEdit: {
			handler(val) {
				if (!val) {
					this.generateSource(this.dataPage, this.pageSize);
				}
			}
		},
		isDesktopDevice: {
			immediate: true,
			handler(val) {
				if (val) {
					Object.assign(this.accordion, {
						first: true,
						second: true
					});
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
  * Impact Form content - Editable Version
  * Can use SCSS variables = TRUE
  * FORM DESIGNER ACCORDIONS
  * FORM BUILDER
  * ADD NEW FIELDS
  * FORM FIELDS
*/

/*------------------------------------*\
  #FORM DESIGNER ACCORDIONS
\*------------------------------------*/

.form-designer-content {
	padding: $padding-md 0 $padding-sm;

	@include mq($until: sm-l) {
		border-top: 1px solid $neutral-3;
	}

	@include mq($from: md) {
		border: none;
		padding: 0;
	}
}

/*------------------------------------*\
  #FORM BUILDER - accordion Section
\*------------------------------------*/
.form-builder {
	padding: $padding-md;
	display: grid;
	gap: $gutter-md $gutter-xl;

	@include mq($from: lg) {
		grid-template-columns: minmax(0, 375px) minmax(0, 1fr);
	}
}

/*------------------------------------*\
  #ADD NEW FIELDS
\*------------------------------------*/
.form-builder__add-fields {
	width: 100%;
	border-radius: $form-border-radius;
	border: 1px solid $neutral-3;
	padding: $padding-md;

	&.is-closed {
		grid-column-end: span 2;
		border: none;
		padding: $padding-md 0;

		.form-builder__expand-btn {
			display: block;
			padding-bottom: $padding-md;
		}

		.form-builder__fields-container {
			display: none;
		}
	}
}

.form-builder__fields-container {
	padding-bottom: $padding-md;
	display: grid;
	gap: $gutter-lg;
}

.form-builder__expand-btn {
	display: none;
}

.form-builder__fields-header {
	display: flex;
	justify-content: space-between;
}

.form-builder__fields-title {
	font-weight: $medium-font-weight;
}

/*------------------------------------*\
  #FORM FIELDS
\*------------------------------------*/
.form-builder__reference {
	@include mq($until: lg) {
		margin-top: $gutter-xl;
	}

	.form-builder__add-fields.is-closed + & {
		grid-column-end: span 2;
	}

	&-toolbar {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		margin-bottom: $gutter-lg;
		flex-direction: column;

		@include mq($from: md) {
			flex-direction: initial;
		}

		.search {
			margin-bottom: $gutter-md;
			@include mq($from: md) {
				width: 300px;
				margin-bottom: 0;
			}
		}
		.toggle {
			justify-content: flex-end;
		}
	}
}
// TODO: UI UX team; reorganize CSS in this file. Was previously split because of two <style> tags
/// Table styles for the Form Fields Section ///
[hidden] {
	display: none !important; //TODO: what reason did dev team need to add this
}

.form-builder {
	&__reference-field {
		position: relative;
	}

	&__type-text {
		display: inline-flex;
		align-items: center;
	}
}

.table-container--form-builder {
	::v-deep .table {
		@include mq($from: md) {
			table-layout: fixed;
		}
	}
	.table__col {
		width: 100%;

		@include mq($from: md) {
			&--field-ref {
				width: auto;
			}
			&--field-type {
				width: 155px;
			}
			&--row,
			&--dot,
			&--column {
				width: 85px;
			}
			&--actions {
				width: 115px;
			}
		}
	}

	::v-deep .badge-count {
		margin-left: $gutter-sm;
	}
}

::v-deep .table__action-btns {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	flex-direction: row;
	justify-content: flex-end;
	padding-right: $padding-xs;

	@include mq($from: sm-l) {
		flex-wrap: nowrap;
	}

	.btn {
		font-size: $default-font-size + 2;
	}
}

::v-deep .table__body .table__tr .table__td {
	@include mq($until: md) {
		align-items: center;
		gap: $gutter-md;
	}

	.field {
		width: 65px; // R/D/C needs to fit 3 digit with arrows from input type number
	}
}

/*------------------------------------*\
  #PRE-ASSIGN CUSTOMERS
\*------------------------------------*/

.pre-assign-customers {
	padding: $padding-md;

	&__description {
		font-size: 14px;
	}
}
</style>
